import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNew from "../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../common/components/custom/hub-header/HubHeader";
import AssignmentMainContent, {
  AssignmentItem
} from "../../../../common/components/custom/assignment-clause-card/AssignmentMainContent";
import ConfirmationModal from "../../../../common/components/custom/confirmation-modal/ConfirmationModal";
import ViewFlowConfigurationModal from "../../../../common/components/custom/assignment-clause-card/ViewFlowConfigurationModal";
import { FlowConfigService } from "../../data/services/flow-config.service";
import { ToastMessageUtil } from "../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../common/enums/controls/toast.enum";
import "./Assignment.scss";
import { Loading } from "modules/_shared/pages";
import { useAppDispatch, useAppSelector } from "../../../../common/redux/core";
import { setSelectedDocumentType } from "../../../_shared/redux/document-type";

const Assignment: React.FC = () => {
  const dispatch = useAppDispatch();

  // Use global Redux state for document type
  const { documentTypeOptions, selectedDocumentType } = useAppSelector(
    (state) => state.documentType
  );

  const [flowConfigurations, setFlowConfigurations] = useState<
    AssignmentItem[]
  >([]);
  const [isLoadingFlowConfig, setIsLoadingFlowConfig] =
    useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [flowConfigError, setFlowConfigError] = useState<string | null>(null);

  const [configurationList, setConfigurationList] = useState<AssignmentItem[]>(
    []
  );

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<
    number | null
  >(null);

  // Fetch flow configurations from API
  const fetchFlowConfigurations = useCallback(async () => {
    setIsLoadingFlowConfig(true);
    setFlowConfigError(null);
    const selectedDocType = documentTypeOptions.filter(
      (dt: any) => dt.value.toString() === selectedDocumentType.toString()
    );
    try {
      const result = await FlowConfigService.getAllFlowConfigurations(
        selectedDocType[0].label
      );

      if (!result.hasErrors && result.value) {
        const transformedConfigs = FlowConfigService.transformToAssignmentItems(
          result.value
        );
        const configsWithDocType = transformedConfigs.map((config) => ({
          ...config,
          documentType: selectedDocumentType // Always use current selectedDocumentType
        }));
        setFlowConfigurations(configsWithDocType);
      } else {
        const errorMessage =
          result.errors.length > 0
            ? result.errors.join(", ")
            : "Failed to fetch flow configurations";
        setFlowConfigError(errorMessage);
        console.error("Flow Config API Error:", result.errors);
        setFlowConfigurations([]);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setFlowConfigError(errorMessage);
      console.error("Error fetching flow configurations:", error);
      setFlowConfigurations([]);
    } finally {
      setIsLoadingFlowConfig(false);
      setIsInitialLoad(false);
    }
  }, [selectedDocumentType]);

  // Load flow configurations on component mount and when document type changes
  useEffect(() => {
    fetchFlowConfigurations();
  }, [fetchFlowConfigurations]);

  // Use flow configurations instead of assignments
  const assignments = flowConfigurations;

  useEffect(() => {
    setConfigurationList(assignments);
  }, [assignments]);
  const navigate = useNavigate();

  const handleEdit = useCallback(
    async (id: number) => {
      try {
        const result = await FlowConfigService.getFlowConfigurationById(id);
        if (!result.hasErrors && result.value) {
          const config = result.value;
          navigate("/configure-ops", {
            state: {
              editMode: true,
              configData: config,
              documentType: selectedDocumentType
            }
          });
        } else {
          ToastMessageUtil.show(
            "Failed to load configuration for editing",
            ToastVariant.ERROR
          );
        }
      } catch (error) {
        ToastMessageUtil.show(
          "Error loading configuration",
          ToastVariant.ERROR
        );
      }
    },
    [navigate, selectedDocumentType]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      // Find the configuration to get its name
      const foundConfig = flowConfigurations.find((config) => config.id === id);
      const configName = foundConfig?.title || `Configuration ${id}`;

      // Set the configuration to delete and show modal
      setConfigToDelete({ id, name: configName });
      setDeleteModalOpen(true);
    },
    [flowConfigurations]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!configToDelete) return;

    setIsDeleting(true);
    try {
      const result = await FlowConfigService.deleteFlowConfiguration(
        configToDelete.id
      );
      if (!result.hasErrors && result.value) {
        setFlowConfigurations((prevConfigs) =>
          prevConfigs.filter((config) => config.id !== configToDelete.id)
        );
        ToastMessageUtil.show(
          `Configuration deleted successfully!`,
          ToastVariant.SUCCESS
        );
        setDeleteModalOpen(false);
        setConfigToDelete(null);
      } else {
        const errorMessage =
          result.errors.length > 0
            ? result.errors.join(", ")
            : "Failed to delete flow configuration";
        ToastMessageUtil.show(
          `Error deleting configuration: ${errorMessage}`,
          ToastVariant.ERROR
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      ToastMessageUtil.show(
        `Error deleting configuration: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setIsDeleting(false);
    }
  }, [configToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setConfigToDelete(null);
  }, []);

  const handleView = useCallback((id: number) => {
    setSelectedConfigurationId(id);
    setViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedConfigurationId(null);
  }, []);

  const handleNewConfiguration = useCallback(async () => {
    try {
      navigate("/configure-ops", {
        state: { documentType: selectedDocumentType }
      });
    } catch (errors) {
      console.error("Error navigating to configure-ops:", errors);
    }
  }, [navigate, selectedDocumentType]);
  const handleDocumentTypeChange = useCallback(
    (documentType: string) => {
      dispatch(setSelectedDocumentType(documentType));
    },
    [dispatch]
  );

  const handleProcessingHub = useCallback(
    (id: number) => {
      navigate("/processing-hub");
    },
    [navigate]
  );

  // Show loading when flow configurations are being loaded (only on initial load)
  if (isInitialLoad && isLoadingFlowConfig) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="assignment-container">
          <HubHeader
            className="header"
            title="Configuration Hub"
            showBackButton={true}
            backRoute="/configure"
          />
          <div className="assignment-content">
            <div className="loading-container">
              <Loading />
            </div>
          </div>
        </div>
      </>
    );
  }

  // if (flowConfigError) {
  //   console.error("Error loading flow configurations:", flowConfigError);
  // }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="assignment-container">
        <HubHeader
          className="header"
          title="Configuration Hub"
          showBackButton={true}
          backRoute="/configure"
        />

        {/* Error Display */}
        {/* {flowConfigError && (
          <div className="error-message">
            <strong>Warning:</strong> {flowConfigError}. Showing fallback data.
          </div>
        )} */}

        {/* Main Content */}
        <div className="assignment-content">
          <AssignmentMainContent
            assignments={assignments}
            configurationList={configurationList}
            setConfigurationList={setConfigurationList}
            showDropdown={false}
            showNewConfigButton={true}
            newConfigButtonText="New configuration"
            onNewConfigClick={handleNewConfiguration}
            cardsClickable={false}
            showActions={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            showProcessingHubBtn={true}
            onProcessingHub={handleProcessingHub}
            showDocumentTypeFilter={true}
            onDocumentTypeChange={handleDocumentTypeChange}
            headerTitle="Available Flow Configurations"
            className="assignment-main-content"
            isLoading={isLoadingFlowConfig}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title="Delete Flow Configuration"
        message={`Are you sure you want to delete "${configToDelete?.name}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
      />

      {/* View Flow Configuration Modal */}
      <ViewFlowConfigurationModal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        configurationId={selectedConfigurationId}
      />
    </>
  );
};

export default Assignment;
